<script>
  // parent *must* provide links to video at 300x300 and 360x360 resolutions

  import { createEventDispatcher, onMount, onDestroy } from 'svelte'
  import { scale, fade } from 'svelte/transition'
  import { quadInOut } from 'svelte/easing'

  export let srcs, captionSrc, playing = false
  
  const dispatch = createEventDispatcher();
  
  let paused = true
  let muted = true
  let ready = false
  let currentTime, duration
  
  let loadingIndicatorClass = "" // for load progress indicator

  let didCancelAutoplay = false

  let videoElement
  let width

  let subtitleTrack
  let activeSpeechCues = []
  let activeKiaiCues = []
  let activeMusicCues = []

  let showCaptions = true

  export let continueAnimationCallbacks = true // workaround -- requestAnimationFrame callback loops will prevent this component from being destroyed by a parent

  // let consoleText = ""
  let windowInnerWidth, newVideoResolutionNeeded, windowInnerHeight, viewportShortSideLength, videoWidth, videoSrc, svgWidth, videoCircumference

  $: { viewportShortSideLength = windowInnerWidth < windowInnerHeight ? windowInnerWidth : windowInnerHeight
  }
  $: videoWidth = viewportShortSideLength >= 374 ? 360 : 300
  $: svgWidth = videoWidth + 20
  $: videoCircumference = Math.floor(videoWidth * Math.PI)
  $: {
    if(videoElement !== undefined){
      newVideoResolutionNeeded = !videoElement.src.includes("" + videoWidth)
      if(newVideoResolutionNeeded){
        didCancelAutoplay = false
        videoSrc = srcs.find( source => source.width == videoWidth).src
      }
    }
  }

  $: {
    if(ready){
      paused = !playing
      // for safari
      subtitleTrack.mode = "hidden"
      // for chrome
      delay(500).then( () => {
        paused = !playing
      })
    }
  }

  let onLoadedFirstFrame = function(event){
    muted = false
    loadingIndicatorClass = "started"
  }

  let onPlaying = function(event){
    // for webkit
    if(!didCancelAutoplay){
      paused = true
      didCancelAutoplay = true
    }
    
    continueAnimationCallbacks = true
    requestAnimationFrame(renderProgressIndicator)
    requestAnimationFrame(scrapeCaptions)
  }

  let onPause = function(event){
    continueAnimationCallbacks = false
  }

  let onCanPlayThrough = async function(event){
    loadingIndicatorClass += " finished"
    await delay(1000)
    ready = true
  }

  let onEnded = function(event){
    dispatch("videoEnded")
  }
  
  onMount( () => { 
    const progressIndicator = document.getElementById('progressIndicator')

    let textTracks
    try { 
      textTracks= videoElement.textTracks
    } catch(error){
      console.log(error)
      return
    }

    if(textTracks != null && textTracks !== undefined && textTracks.length > 0){
      // consoleText = "text tracks: " + videoElement.textTracks.length + ", mode: " + videoElement.textTracks[0].mode
      videoElement.textTracks[0].mode = "hidden"
      // consoleText += ", mode after onMount: " + videoElement.textTracks[0].mode
      subtitleTrack = textTracks[0]
    } else {
      console.log(new Error("No text tracks found"))
    }
    requestAnimationFrame(scrapeCaptions)
  })

  onDestroy( () => {
    continueAnimationCallbacks = false
  })

  let scrapeCaptions = function(){
    let speechCues, kiaiCues, musicCues

    speechCues = []  // instructor's speech
    kiaiCues = []    // instructor's spoken musical guidance (ie "don", "so-re")
    musicCues = []   // beats played by the instructor
    
    if(subtitleTrack != null && subtitleTrack !== undefined 
      && subtitleTrack.activeCues != null && subtitleTrack.activeCues !== undefined
      && subtitleTrack.activeCues.length > 0){
        
      let activeCuesList = subtitleTrack.activeCues

      if(activeCuesList != null && activeCuesList !== undefined && activeCuesList.length > 0){
        for(var i = 0; i < activeCuesList.length; i++){
          const cue = activeCuesList[i]
          let cueText

          if(cue){
            cueText = cue.text
            if(cueText){
              // music cues
              const matches = cueText.match(/^♪\s*(?<musicCue>[a-zA-Z\-\s]*)\s*♪$|^(?<kiaiCue>"[a-zA-Z\-\s]*"$)/)
              if(matches != null){
                if(matches.groups["musicCue"] !== undefined){
                  let cue = matches.groups["musicCue"].replace(/\s+$/g, "") // remove trailing space(s)
                  cue = cue.split(" ").map( word => "{" + word + "}") // wrap in curly braces because in the music caption font, curly braces present as SFX indicators
                  musicCues.push(...cue) 
                } else if(matches.groups["kiaiCue"] !== undefined){
                  kiaiCues.push(matches.groups["kiaiCue"])
                } else {
                  throw new Error("Failed to parse caption: " + cueText)
                }
              } else {
                speechCues.push(cueText)
              }
            }
          }
        }
      }
    } 

    // make sure caption content has actually changed before triggering an expensive update via Svelte

    if(speechCues.length != activeSpeechCues.length || speechCues.join(",") 
    != activeSpeechCues.join(",")){
      console.log(speechCues)
      activeSpeechCues = speechCues
    }

    if(kiaiCues.length != activeKiaiCues.length || kiaiCues.join(",") 
    != activeKiaiCues.join(",")){
      console.log(kiaiCues)
      activeKiaiCues = kiaiCues
    } 

    if(musicCues.length != activeMusicCues.length || musicCues.join(",") 
    != activeMusicCues.join(",")){
      console.log(musicCues)
      activeMusicCues = musicCues
    }

    if(continueAnimationCallbacks){
      requestAnimationFrame(scrapeCaptions) 
    }
  }

  let renderProgressIndicator = function(){
    const progress = currentTime/duration
    if(progress !== undefined && progress > 0){
      progressIndicator.style.strokeDashoffset = Math.round(videoCircumference - (progress * videoCircumference))
    }
    if(!paused && continueAnimationCallbacks){
      requestAnimationFrame(renderProgressIndicator)
    }
  }

  /* CUSTOM TRANSITIONS */
  function wipeIn(node, { duration }) {
		return {
			duration,
			css: (t,u) => {
        const eased = quadInOut(u);
        return `
					-webkit-clip-path: inset(0% ${Math.floor(eased*100)}% 0% 0%);`

				// return `
				// 	-webkit-clip-path: inset(0% ${Math.floor(eased*100)}% 0% 0%);
				// 	color: hsl(
				// 		${~~(t * 360)},
				// 		${Math.min(100, 1000 - 1000 * t)}%,
				// 		${Math.min(50, 500 - 500 * t)}%
				// 	);`
			}
		};
	}
	
	function wipeOut(node, { duration }) {
		return {
			duration,
			css: (t,u) => {
				const eased = quadInOut(u);

				return `
					-webkit-clip-path: inset(0% 0% 0% ${Math.floor(eased*100)}%);`
			}
		};
	}
  /* UTILITIES */
	const delay = function(time){ // time in ms
		return new Promise( resolve => setTimeout(resolve, time))
	}
  
</script>

<svelte:window bind:innerWidth={windowInnerWidth} bind:innerHeight={windowInnerHeight}/>
<div class="video-container" 
  in:scale="{{ delay: 250, duration: 750 }}"
  out:scale="{{duration: 500}}"
>
  <svg viewbox="{-svgWidth/2} {-svgWidth/2} {svgWidth} {svgWidth}" width="{svgWidth}" height="{svgWidth}">
    <circle cx=0 cy=0 r={videoWidth/2} fill="transparent" stroke="green" id="loadingIndicator" class={loadingIndicatorClass}/>
    <circle cx=0 cy=0 r={videoWidth/2} fill="transparent" stroke="rgba(0, 0, 255, 0.5)" id="progressIndicator"/>
  </svg>
  <video src={videoSrc}
    bind:currentTime
    bind:duration
    bind:this={videoElement}
    bind:paused on:ended={onEnded}
    on:click={ e => paused = !paused }
    playsinline width="{videoWidth}" height="{videoWidth}"
    bind:muted 
    autoplay preload
    on:loadeddata={onLoadedFirstFrame}
    on:playing={onPlaying}
    on:pause={onPause}
    on:canplaythrough={onCanPlayThrough}
    crossorigin="anonymous"
  >
    <track label="English" kind="subtitles" srclang="en" src={captionSrc} default/>
  </video>

  {#if activeMusicCues.length > 0}
    <div id="music_captions">
      {#each activeMusicCues as musicCaption}
        <div class="music-caption" transition:fade={{duration: 100, ease: 'sineInOut'}}>{ musicCaption }</div>
      {/each}
    </div>
  {/if}

  {#if activeKiaiCues.length > 0}
    <div id="kiai_captions">
      {#each activeKiaiCues as kiaiCaption}
        <div class="kiai-caption" transition:fade={{duration: 100, ease: 'sineInOut'}}>{ kiaiCaption }</div>
      {/each}
    </div>
  {/if}
  
  <button type="button" class="btn-image btn-sm btn-captions" class:on="{showCaptions}" on:click={ e => { showCaptions = !showCaptions}}><span class="label">Captions</span></button>

  {#if showCaptions}
    <div class="subtitles" transition:fade="{{duration: 200}}">
      {#each activeSpeechCues as caption}
        <p class="size-h2 pov-first-person" transition:fade="{{duration:100}}">{caption}</p>
      {/each}
    </div>
  {/if}

  <!-- <p class="console">{consoleText}</p> -->
</div>

<style>
  .video-container {
    position: relative;
    flex: 0 0 auto;
    width: 380px;
    height: 380px;
  }

  video {
    position: absolute;
    top: 10px;
    left: 10px;
    /* background-color: aqua; */
    /* flex: 0 0 auto; */
    clip-path: circle(50%);
    -webkit-clip-path: circle(50%)
  }

  button.btn-captions {
    position: absolute;
    top: -9px;
    left: 20px;
    margin-bottom: 0;
  }

  svg {
    position: absolute;
    top: 0;
    left: 0;
  }

  svg > circle {
    stroke-width: 10;
    stroke-dasharray: 1131;
    stroke-dashoffset: 1131;
  }

  svg > circle.started {
    stroke-dashoffset: 100;
    transition: 10s;
  }

  svg > circle.started.finished {
    stroke-dashoffset: 0;
    transition: 1s;
  }

  .subtitles {
    position: absolute;
    text-align: center;
    width: 100%;
    display: flex;
    justify-content: center;
    pointer-events: none;
  }

  .subtitles > p {
    background-color: rgba(255, 255, 255, 0.6);
    display: inline-block;
    padding: 0.1em 0.5em;
    border-radius: 0.3em;
    color: #000;
  }

  .subtitles > p:last-child {
    margin-bottom: 0;
  }

  #music_captions {
    position: absolute;
    bottom: 20%;
    left: 45%;
    display: flex;
    justify-content: end;
    align-items: flex-end;
    flex-direction: column;
    flex-wrap: nowrap;
    pointer-events: none;
  } 
  
  .music-caption {
    flex: 0 0 auto;
    margin: 0;
		padding: 0;
		display: inline-block;
    transform: rotate(-35deg);
    font-family: 'Crash Landing Italic';
    color: #000;
    text-transform: uppercase;
    font-size: 4em;
    line-height: 1em;
    pointer-events: none;
    text-shadow: 
      0.04em 0.04em white, 
      -0.04em -0.04em white,
      0.04em -0.04em white, 
      -0.04em 0.04em white,
      0.08em 0.08em 2px rgba(0, 0, 0, 0.5);
  }
  
  /* Safari only (chrome doesn't actually support paint-order) */
  @media not all and (min-resolution:.001dpcm) { 
    .music-caption {
      -webkit-text-stroke: 0.15em white;  
      paint-order: stroke fill;
      text-shadow: 0.08em 0.08em 2px rgba(0, 0, 0, 0.5);
    }
  }

  #kiai_captions {
    position: absolute;
    left: 60%;
    top: 15%;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    pointer-events: none;
  }

  .kiai-caption {
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
    font-family: 'Crash Landing';
    font-size: 2.5em;
    pointer-events: none;
    text-transform: uppercase;
    transform: rotate(-15deg);
    color: #C74022;
    text-shadow:
      0.04em 0.04em black, 
      -0.04em -0.04em black,
      0.04em -0.04em black, 
      -0.04em 0.04em black,
      0.08em 0.08em 2px rgba(0, 0, 0, 0.5);
  }

  /* Safari only (chrome doesn't actually support paint-order) */
  @media not all and (min-resolution:.001dpcm) { 
    .kiai-caption {
      -webkit-text-stroke: 0.15em black;
      paint-order: stroke fill;
      text-shadow: 0.08em 0.08em 2px rgba(0, 0, 0, 0.5)
    }
  }

  /* p.console {
    background: black;
    color: white;
    text-align: center;
    width: 100%;
    position: absolute;
    top: 2em;
    pointer-events: none;
  } */

  @media (orientation: portrait) and (max-width: 373px), (orientation: landscape) and (max-height: 373px){
    /* not triggered on mobile Safari iphone 6s landscape! */
    .video-container {
      width: 320px;
      height: 320px;
    }

    svg > circle {
      stroke-width: 10;
      stroke-dasharray: 942;
      stroke-dashoffset: 942;
    }
  }

  @media (orientation: portrait){
    .subtitles {
      bottom: -80px;
    }
  }

  @media (orientation: landscape){
    button.btn-captions {
      top: 10px;
      left: -5px;
    }

    .subtitles {
      bottom: 0;
    }
  }
</style>